import { cx } from '@linaria/core';
import { useMemo } from 'react';
import { CourseItem, SimplifiedHeader, makePreviewLightboxConfig, courseBuilderStore } from '@pm2/course-builder';
import { useNotifier } from '@provider/notifier';
import { useTrackingManager } from '@provider/tracking';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Table } from '@ui/kit';
import { configRefs } from '@utils/constants';
import { ShowFor, useMatchBreakpoint } from '@utils/react';
import { isNotificationConsideredRecent } from '../../notifications/util.is-considered-recent.js';
import { useSortedCourses } from '../hooks/sorted-courses.js';
import { ComplexHeader } from './course-table.header.complex.js';
import { wrapperCls, actionsDisabledMessageCls, table, row } from './course-table.style.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useDeleteNotificationMutation } from '../../notifications/api.delete-notification.mutation.js';

function CourseTable(props) {
  const [sortedCourses, applySort, options] = useSortedCourses(props.courses, props.filterOptions);
  const user = useUserIdentityState(state => state.user);
  const preparedCourses = useMemo(() => sortedCourses.map(it => ({
    ...it,
    key: it.__internalId
  })), [sortedCourses]);
  return /*#__PURE__*/jsxs("div", {
    className: wrapperCls,
    children: [user?.role === "teacher" && /*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.mobile.andDown(),
      children: /*#__PURE__*/jsx("span", {
        className: actionsDisabledMessageCls,
        children: "Det er ikke muligt at bygge forl\xF8b fra mobilen."
      })
    }), /*#__PURE__*/jsx(Table, {
      className: table,
      thead: /*#__PURE__*/jsx(Thead, {
        ...props,
        applySort: applySort,
        options: options
      }),
      contentKey: `${options.section}-${options.query}-${options.direction}-${options.sortBy}`,
      emptyMessage: /*#__PURE__*/jsx(EmptyMessage, {
        options: options
      }),
      items: preparedCourses,
      Item: Item
    })]
  });
}
function Thead(props) {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.mobile.andDown(),
      children: /*#__PURE__*/jsx(SimplifiedHeader, {})
    }), /*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.tablet.andUp(),
      children: /*#__PURE__*/jsx(ComplexHeader, {
        applySort: props.applySort,
        options: props.options
      })
    })]
  });
}
function EmptyMessage(props) {
  return props.options.query ? /*#__PURE__*/jsx(Fragment, {
    children: "Ingen forl\xF8b matcher din s\xF8gning"
  }) : /*#__PURE__*/jsx(Fragment, {
    children: "Der er ingen tilg\xE6ngelige forl\xF8b"
  });
}
function Item(props) {
  const tracker = useTrackingManager();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  const isMobile = useMatchBreakpoint(breakpoints.mobile.andDown(), {
    immediate: true
  });
  const deleteNotification = useDeleteNotificationMutation();
  const simpleLayout = user?.role === "student";
  const onRootClick = () => {
    tracker.trackEvent("userarea-delinger.builder.userarea.clickedCourse");
    if (props.item.shareNotification) {
      deleteNotification.mutate([props.item.shareNotification]);
    }

    // In a simple layout the preview of the course should simply
    // be opened...
    if (isMobile) {
      window.open(`${configRefs.urls.courses.dangerouslyAssertSyncValue()}/viser/${props.item?.id}`, "_blank");
    } else if (simpleLayout) {
      notifier.modal(makePreviewLightboxConfig({
        course: props.item
      }));
    } else {
      // ... however, in a complex view the simplified course
      // builder should open instead
      courseBuilderStore.dispatch(courseBuilderStore.actions.selectCourseById(props.item.id));
      courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel(true));
    }
  };
  const onActionClick = () => {
    if (!props.item.shareNotification) {
      return;
    }
    deleteNotification.mutate([props.item.shareNotification]);
  };
  const isNew = props.item.shareNotification && isNotificationConsideredRecent(props.item.shareNotification);
  return /*#__PURE__*/jsx(CourseItem, {
    className: cx(row, props.item.shareNotification && "hasNotification"),
    onCourseClick: onRootClick,
    onActionClick: onActionClick,
    item: props.item,
    simpleLayout: simpleLayout,
    mobileLayout: isMobile,
    new: isNew
  });
}

export { CourseTable };
//# sourceMappingURL=course-table.js.map
